import React from 'react';
import {
	IMagicLinkSettings,
	IOTPSettings,
	IPasswordSettings
} from 'types/settings';
import { IExtendedForm, ProviderPrefix } from 'types/connectors';
import { getDisplayFieldId } from '@utils/connectors';
import { Alert, IconButton, List, Fade, ListItem, Button } from '@mui/material';
import { GridCloseIcon } from '@mui/x-data-grid';
import { AuthMethodRoute } from 'constants/routes';
import { DynamicValues } from 'constants/dynamicValues';
import { IHelperTextProp, IHelpTextItem } from 'types/tooltips';
import { Translate } from 'types/common';
import { FORM_KEYS } from '../CommonFormKeys';

interface IDynamicHelperData {
	example: string;
	children: IHelpTextItem[];
}

const MAGIC_LINK_SMS_DYNAMIC_VALUES = [DynamicValues.redirect.URL];

const MAGIC_LINK_EMAIL_DYNAMIC_VALUES = [
	DynamicValues.redirect.URL,
	DynamicValues.project.NAME,
	DynamicValues.auth.TOKEN,
	DynamicValues.auth.EXP_TIME
];

const ENCHANTED_LINK_EMAIL_DYNAMIC_VALUES = [
	DynamicValues.redirect.KEY_1,
	DynamicValues.redirect.KEY_2,
	DynamicValues.redirect.KEY_3
];

const OTP_EMAIL_DYNAMIC_VALUES = [
	DynamicValues.auth.CODE,
	DynamicValues.auth.EXP_TIME,
	DynamicValues.auth.DOMAIN
];

const OTP_SMS_DYNAMIC_VALUES = [
	DynamicValues.auth.CODE,
	DynamicValues.auth.EXP_TIME,
	DynamicValues.auth.DOMAIN
];

const getEmailDyanmicValuesHelperData = (
	pageId: AuthMethodRoute
): IDynamicHelperData => {
	switch (pageId) {
		case AuthMethodRoute.EnchantedLink:
			return {
				example:
					'connectors.template.dynamicValues.enchantedlink.email.example',
				children: [
					{ title: '', children: MAGIC_LINK_EMAIL_DYNAMIC_VALUES },
					{
						title:
							'connectors.template.dynamicValues.enchantedlink.redirection.title',
						children: ENCHANTED_LINK_EMAIL_DYNAMIC_VALUES
					}
				]
			};
		case AuthMethodRoute.MagicLink:
			return {
				example: 'connectors.template.dynamicValues.magiclink.email.example',
				children: [
					{
						children: MAGIC_LINK_EMAIL_DYNAMIC_VALUES
					}
				]
			};
		case AuthMethodRoute.OTP:
			return {
				example: 'connectors.template.dynamicValues.otp.email.example',
				children: [{ children: OTP_EMAIL_DYNAMIC_VALUES }]
			};
		default:
			return { example: '', children: [] };
	}
};

const getSmsDyanmicValuesHelperData = (
	pageId: AuthMethodRoute
): IDynamicHelperData => {
	switch (pageId) {
		case AuthMethodRoute.MagicLink:
			return {
				example: 'connectors.template.dynamicValues.magiclink.sms.example',
				children: [
					{
						children: MAGIC_LINK_SMS_DYNAMIC_VALUES
					}
				]
			};
		case AuthMethodRoute.OTP:
			return {
				example: 'connectors.template.dynamicValues.otp.sms.example',
				children: [{ children: OTP_SMS_DYNAMIC_VALUES }]
			};
		default:
			return { example: '', children: [] };
	}
};

const getProviderTooltipData = (
	type: ProviderPrefix,
	pageId: AuthMethodRoute
) => {
	switch (type) {
		case ProviderPrefix.EMAIL:
			return getEmailDyanmicValuesHelperData(pageId);
		case ProviderPrefix.SMS:
			return getSmsDyanmicValuesHelperData(pageId);
		default:
			return { example: '', children: [] };
	}
};

export interface IAlertItem {
	key: string;
	label: string;
	callback: () => void;
}

export interface IAlertItemsProps {
	message: string;
	items: IAlertItem[];
	fadeInAlert?: boolean;
	onClose: () => void;
}

export const getFieldDynamicKey = (key: string, provider: string) => {
	switch (key) {
		case FORM_KEYS.emailBody:
			return getDisplayFieldId({
				fieldName: FORM_KEYS.emailBody,
				customFieldName: FORM_KEYS.customEmailBody,
				provider
			});
		case FORM_KEYS.emailSubject:
			return getDisplayFieldId({
				fieldName: FORM_KEYS.emailSubject,
				customFieldName: FORM_KEYS.customEmailSubject,
				provider
			});
		case FORM_KEYS.textMessageBody:
			return getDisplayFieldId({
				fieldName: FORM_KEYS.textMessageBody,
				customFieldName: FORM_KEYS.customTextMessageBody,
				provider
			});
		default:
			return 'NO_ID_FOUND';
	}
};

export type BodyValueEntry = {
	id: string;
	value: string;
};

export const getEmailBodyInputValues = (
	vals: IExtendedForm<IMagicLinkSettings | IOTPSettings | IPasswordSettings>,
	customVals: any
): BodyValueEntry[] => [
	{
		id: FORM_KEYS.emailBody,
		value: vals.emailBody
	},
	{
		id: FORM_KEYS.customEmailBody,
		value: customVals?.emailBody || vals?.emailBody
	}
];

export const getTextBodyInputValues = (
	vals: IExtendedForm<IMagicLinkSettings | IOTPSettings>
): BodyValueEntry[] => [
	{
		id: FORM_KEYS.textMessageBody,
		value: vals.textMessageBody as string
	},
	{
		id: FORM_KEYS.customTextMessageBody,
		value: vals.customTextMessageBody as string
	}
];

export const getConnectorsDisabledTemplateAlert = ({
	message,
	items,
	fadeInAlert,
	onClose
}: IAlertItemsProps) => (
	<Fade in={fadeInAlert} appear timeout={500}>
		<Alert
			severity="info"
			action={
				<IconButton
					aria-label="close"
					color="inherit"
					size="small"
					onClick={onClose}
				>
					<GridCloseIcon fontSize="inherit" />
				</IconButton>
			}
		>
			{intl.tr(message)}
			<List>
				{items.map((item) => (
					<ListItem disablePadding key={`connector-notification-${item.key}`}>
						<Button size="small" onClick={item.callback}>
							{intl.tr(item.label)}
						</Button>
					</ListItem>
				))}
			</List>
		</Alert>
	</Fade>
);

export const getDynamicValuesTooltip = ({
	tr,
	pageId,
	providerType
}: {
	tr: Translate;
	pageId: AuthMethodRoute;
	providerType: ProviderPrefix;
}): IHelperTextProp => {
	const { example, children } = getProviderTooltipData(providerType, pageId);
	return {
		meta: {
			example: tr(example),
			trigger: tr('button.usingDynamicValues')
		},
		content: {
			header: tr('connectors.template.dynamicValues.header')
		},
		children: children.map((group) => ({
			title: group.title && tr(group.title),
			children: group.children
		}))
	};
};
